import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../apiConfig';

const MockDepositPage = () => {
  const [mockTrades, setMockTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [solPrice, setSolPrice] = useState(null);
  const [loadingSolPrice, setLoadingSolPrice] = useState(true);
  const [depositAmount, setDepositAmount] = useState('');
  const [depositing, setDepositing] = useState(false);
  const [depositHistory, setDepositHistory] = useState([]);
  const [totalMockSOL, setTotalMockSOL] = useState(1.0);

  // Fetch SOL price from CoinGecko
  const fetchSolPrice = async () => {
    try {
      setLoadingSolPrice(true);
      const response = await axios.get('https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd');
      if (response.data.solana && response.data.solana.usd) {
        setSolPrice(response.data.solana.usd);
      } else {
        setSolPrice(null);
      }
    } catch (error) {
      console.error('Failed to fetch SOL price:', error);
      setSolPrice(null);
    } finally {
      setLoadingSolPrice(false);
    }
  };

  // Fetch mock trades from backend
  const fetchMockTrades = async (isInitialLoad = false) => {
    try {
      if (isInitialLoad) {
        setLoading(true);
      }
      const apiUrl = getApiUrl();
      const response = await axios.get(`${apiUrl}/mock-trades`);
      setMockTrades(response.data || []);
      setError(null);
    } catch (error) {
      console.error('Failed to fetch mock trades:', error);
      setError('Failed to fetch mock trades');
      setMockTrades([]);
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  };

  // Helper function to check if trade has buy transaction icon link
  const hasBuyTransactionIcon = (trade) => {
    return !!trade.transaction_id && !!trade.buy_time;
  };

  // Calculate current mock SOL balance
  const calculateMockSOL = () => {
    // Calculate Total Mock SOL balance properly accounting for all transactions
    
    // Start with initial balance (reset to 0.0 SOL)
    let balance = 0.0;
    
    // Add any deposits from localStorage
    const storedDeposits = JSON.parse(localStorage.getItem('mockDeposits') || '[]');
    const totalDeposits = storedDeposits.reduce((sum, deposit) => sum + deposit.amount, 0);
    balance += totalDeposits;
    
    // Subtract SOL used for trades
    const activeCount = mockTrades.filter(trade => hasBuyTransactionIcon(trade) && !trade.sell_time).length;
    const completedCount = mockTrades.filter(trade => hasBuyTransactionIcon(trade) && trade.sell_time).length;
    const tradeAmount = 0.015; // Amount used per trade (matches backend STATIC_DEPOSIT_AMOUNT)
    const totalTradesUsed = activeCount + completedCount;
    balance -= totalTradesUsed * tradeAmount;
    
    // Add back actual SOL received from completed trades
    const completedTrades = mockTrades.filter(trade => trade.sell_time);
    completedTrades.forEach(trade => {
      const profitLoss = trade.profit_loss || 0;
      
      // Calculate actual SOL received based on profit/loss
      const actualSolReceived = tradeAmount * (1 + (profitLoss / 100));
      balance += actualSolReceived;
    });
    
    // Handle partial sales if they exist
    mockTrades.forEach(trade => {
      if (trade.partial_sales && trade.partial_sales.length > 0) {
        trade.partial_sales.forEach(sale => {
          const profitLoss = sale.profit_loss || 0;
          // Calculate actual SOL received from partial sale
          let actualSolReceived;
          if (sale.amount && sale.amount > 0) {
            // If sale.amount is already calculated correctly, use it
            actualSolReceived = sale.amount;
          } else {
            // Calculate based on profit/loss percentage
            actualSolReceived = tradeAmount * (1 + (profitLoss / 100));
          }
          balance += actualSolReceived;
        });
      }
    });
    
    // Ensure balance doesn't go below 0
    return Math.max(0, balance);
  };

  // Format USD value
  const formatUsdValue = (solAmount) => {
    if (!solAmount || !solPrice) return '$0.00';
    const usdValue = parseFloat(solAmount) * solPrice;
    return `$${usdValue.toFixed(2)}`;
  };

  // Handle deposit
  const handleDeposit = async () => {
    const amount = parseFloat(depositAmount);
    
    if (!amount || amount <= 0) {
      alert('Please enter a valid deposit amount');
      return;
    }
    
    if (amount > 100) {
      alert('Maximum deposit amount is 100 SOL');
      return;
    }
    
    try {
      setDepositing(true);
      
      // Create deposit record
      const deposit = {
        id: Date.now(),
        amount: amount,
        timestamp: new Date().toISOString(),
        type: 'deposit',
        description: `Deposited ${amount} SOL to Mock Portfolio`
      };
      
      // Store in localStorage
      const existingDeposits = JSON.parse(localStorage.getItem('mockDeposits') || '[]');
      existingDeposits.push(deposit);
      localStorage.setItem('mockDeposits', JSON.stringify(existingDeposits));
      
      // Update deposit history
      setDepositHistory(existingDeposits);
      
      // Clear form
      setDepositAmount('');
      
      // Show success message
      alert(`Successfully deposited ${amount} SOL to Mock Portfolio!`);
      
    } catch (error) {
      console.error('Failed to process deposit:', error);
      alert('Failed to process deposit. Please try again.');
    } finally {
      setDepositing(false);
    }
  };

  // Load deposit history from localStorage
  const loadDepositHistory = () => {
    const storedDeposits = JSON.parse(localStorage.getItem('mockDeposits') || '[]');
    setDepositHistory(storedDeposits);
  };

  // Format time to UTC+1
  const formatTimeUTC1 = (timeString) => {
    if (!timeString) return 'N/A';
    const date = new Date(timeString);
    date.setHours(date.getHours() + 1);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchMockTrades(true); // Initial load
    fetchSolPrice(); // Fetch SOL price
    loadDepositHistory(); // Load deposit history
    
    // Set up periodic refresh
    const interval = setInterval(() => {
      fetchMockTrades(false); // Background refresh
    }, 5000);
    
    // Set up SOL price refresh every 30 seconds
    const solPriceInterval = setInterval(() => {
      fetchSolPrice();
    }, 30000);
    
    return () => {
      clearInterval(interval);
      clearInterval(solPriceInterval);
    };
  }, []);

  // Update mock SOL balance when trades or deposits change
  useEffect(() => {
    setTotalMockSOL(calculateMockSOL());
  }, [mockTrades, depositHistory]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-16 w-16 border-2 border-transparent" style={{ borderTopColor: '#22c55e', borderRightColor: '#22c55e' }}></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-400 text-lg">{error}</p>
        <button 
          onClick={fetchMockTrades}
          className="mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Mock Deposit Header */}
      <div className="backdrop-blur-sm rounded-xl p-6" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)' }}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">Mock SOL Deposit</h2>
          <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-900/50 text-yellow-400">
            SIMULATION MODE
          </span>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="backdrop-blur-sm rounded-lg p-4 text-center" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="text-sm text-slate-400 mb-2">Current Mock SOL</div>
            <div className="text-2xl font-bold text-purple-400">
              {totalMockSOL.toFixed(4)}
            </div>
            <div className="text-xs text-slate-500 mt-1">Available Balance</div>
          </div>
          
          <div className="backdrop-blur-sm rounded-lg p-4 text-center" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="text-sm text-slate-400 mb-2">SOL Price (Live)</div>
            <div className="text-2xl font-bold text-blue-400">
              {loadingSolPrice ? 'Loading...' : solPrice ? `$${solPrice.toFixed(2)}` : 'N/A'}
            </div>
            <div className="text-xs text-slate-500 mt-1">Real-time Price</div>
          </div>
          
          <div className="backdrop-blur-sm rounded-lg p-4 text-center" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="text-sm text-slate-400 mb-2">Portfolio Value</div>
            <div className="text-2xl font-bold text-green-400">
              {loadingSolPrice ? 'Loading...' : formatUsdValue(totalMockSOL)}
            </div>
            <div className="text-xs text-slate-500 mt-1">Simulated Value</div>
          </div>
        </div>
      </div>

      {/* Mock Trading Info Banner */}
      <div className="mb-6 p-4 bg-yellow-900/20 border border-yellow-500/30 rounded-lg">
        <div className="flex items-center space-x-2">
          <span className="text-yellow-400 text-lg">💳</span>
          <div>
            <h4 className="text-yellow-400 font-semibold">Mock SOL Deposit</h4>
            <p className="text-yellow-300 text-sm">
              Deposit simulated SOL to your mock portfolio for testing trading strategies. 
              This is completely simulated - no real SOL is involved.
            </p>
            <p className="text-yellow-200 text-xs mt-1">
              Deposits are stored locally and will be added to your mock trading balance.
            </p>
          </div>
        </div>
      </div>

      {/* Deposit Form */}
      <div className="backdrop-blur-sm rounded-xl p-6" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)' }}>
        <h3 className="text-xl font-semibold text-white mb-6">Deposit Mock SOL</h3>
        
        <div className="max-w-md mx-auto">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Deposit Amount (SOL)
              </label>
              <div className="relative">
                <input
                  type="number"
                  step="0.001"
                  min="0.001"
                  max="100"
                  value={depositAmount}
                  onChange={(e) => setDepositAmount(e.target.value)}
                  placeholder="Enter amount to deposit"
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <div className="absolute right-3 top-3 text-slate-400 text-sm">
                  SOL
                </div>
              </div>
              <div className="text-xs text-slate-500 mt-1">
                Minimum: 0.001 SOL | Maximum: 100 SOL
              </div>
            </div>
            
            {depositAmount && parseFloat(depositAmount) > 0 && (
              <div className="bg-slate-800/50 border border-slate-600 rounded-lg p-4">
                <div className="text-sm text-slate-400 mb-2">Deposit Preview</div>
                <div className="flex justify-between items-center">
                  <span className="text-white font-medium">
                    {parseFloat(depositAmount)} SOL
                  </span>
                  <span className="text-green-400 font-medium">
                    {loadingSolPrice ? 'Loading...' : formatUsdValue(depositAmount)}
                  </span>
                </div>
              </div>
            )}
            
            <button
              onClick={handleDeposit}
              disabled={depositing || !depositAmount || parseFloat(depositAmount) <= 0}
              className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                depositing || !depositAmount || parseFloat(depositAmount) <= 0
                  ? 'bg-slate-600 text-slate-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {depositing ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-transparent border-t-current"></div>
                  <span>Processing Deposit...</span>
                </div>
              ) : (
                'Deposit Mock SOL'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Deposit History */}
      {depositHistory.length > 0 && (
        <div className="backdrop-blur-sm rounded-xl p-6" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)' }}>
          <h3 className="text-xl font-semibold text-white mb-6">Deposit History</h3>
          
          <div className="space-y-3">
            {depositHistory.slice().reverse().map((deposit, index) => (
              <div key={deposit.id} className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="text-2xl">💳</div>
                    <div>
                      <h4 className="text-lg font-semibold text-blue-400">
                        {deposit.description}
                      </h4>
                      <div className="flex items-center space-x-4 mt-1">
                        <div className="text-sm">
                          <span className="text-slate-400">Amount:</span>
                          <span className="ml-1 font-medium">{deposit.amount} SOL</span>
                        </div>
                        <div className="text-sm">
                          <span className="text-slate-400">USD:</span>
                          <span className="ml-1 font-medium">
                            {loadingSolPrice ? 'Loading...' : formatUsdValue(deposit.amount)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className="text-sm text-slate-400 mb-1">Date & Time</div>
                    <div className="font-medium">
                      {formatTimeUTC1(deposit.timestamp)}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      UTC+1
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Deposit Information */}
      <div className="backdrop-blur-sm rounded-xl p-6" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)' }}>
        <h3 className="text-lg font-semibold text-white mb-4">Deposit Information</h3>
        <div className="space-y-3 text-sm text-slate-300">
          <div className="flex items-start space-x-2">
            <span className="text-blue-400">•</span>
            <span>Mock deposits are stored locally in your browser and will persist between sessions</span>
          </div>
          <div className="flex items-start space-x-2">
            <span className="text-blue-400">•</span>
            <span>Deposited SOL will be added to your mock trading balance</span>
          </div>
          <div className="flex items-start space-x-2">
            <span className="text-blue-400">•</span>
            <span>Mock trades will deduct 0.015 SOL per trade from your balance</span>
          </div>
          <div className="flex items-start space-x-2">
            <span className="text-blue-400">•</span>
            <span>Profitable trades will add SOL back to your balance</span>
          </div>
          <div className="flex items-start space-x-2">
            <span className="text-blue-400">•</span>
            <span>This is completely simulated - no real SOL is involved</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MockDepositPage;
